import React, { useMemo } from 'react';
import { computeTotals, fmtC, formatPeriod } from './helpers';
import { useSearchOrderInvoice } from './hooks/useSearchOrderInvoice';
import FilterBar from './FilterBar';
import OrderRow from './OrderRow';
import styles from './PaymentInvoice.module.css';

const OrdersTable = ({
  userId,
  companyInvoiceId,
  period,
  showKravein,
  onSelectOrder,
}) => {
  const {
    status,
    orders,
    totalOrders,
    totalPages,
    page,
    pageSize,
    search,
    typeFilter,
    setSearch,
    setTypeFilter,
    setPage,
    apply,
  } = useSearchOrderInvoice(userId, companyInvoiceId);

  const totals = useMemo(() => computeTotals(orders), [orders]);
  const periodLabel = formatPeriod(period);
  const isLoading = status === 'loading';
  const colCount = showKravein ? 16 : 12;

  return (
    <div className={`card ${styles.card} ${styles.tableWrap}`}>
      <div className={styles.tableHeader}>
        <div className={styles.tableTitle}>
          Order Transactions{periodLabel ? ` — ${periodLabel}` : ''}
        </div>
        <div className={styles.tableMeta}>
          {totalOrders ?? 0} orders ·{' '}
          <span className={styles.clickHint}>Click row to view order</span>
        </div>
      </div>

      <FilterBar
        searchQuery={search}
        onSearchChange={e => setSearch(e.target.value)}
        typeFilter={typeFilter}
        onTypeChange={e => setTypeFilter(e.target.value)}
        onApply={apply}
      />

      {isLoading ? (
        <div className={styles.tableLoader}>Loading orders…</div>
      ) : (
        <div className={styles.tableScroll}>
          <table className={`table is-fullwidth ${styles.ordTable}`}>
            <thead>
              <tr>
                <th className={styles.colIdx}>#</th>
                <th className={styles.colOrderNo}>Order No.</th>
                <th className={styles.colDate}>Date &amp; Time</th>
                <th className={styles.colType}>Type</th>
                <th className={styles.colSubtotal}>Subtotal</th>
                <th className={styles.colPromo}>Promo</th>
                <th className={styles.colAdjustment}>Adjustment</th>
                <th className={styles.colSurcharges}>Venue Charges</th>
                <th className={styles.colSaleValue}>Sale Value</th>
                <th className={styles.colKrvFee}>KRV Fee</th>
                <th className={styles.colNetPayout}>Net Payout</th>
                {showKravein && (
                  <>
                    <th className={styles.kraveinColHeader}>KRV Charges</th>
                    <th className={styles.kraveinColHeader}>Delivery Fee</th>
                    <th className={styles.kraveinColHeader}>Card Fee</th>
                    <th className={styles.kraveinColHeader}>KRAVEiN Payout</th>
                  </>
                )}
                <th className={styles.colActions} />
              </tr>
            </thead>

            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan={colCount} className={styles.emptyRow}>
                    No orders found.
                  </td>
                </tr>
              ) : (
                orders.map((order, idx) => (
                  <OrderRow
                    key={order.orderReference}
                    order={order}
                    index={page * pageSize + idx}
                    showKravein={showKravein}
                    onView={onSelectOrder}
                  />
                ))
              )}
            </tbody>

            <tfoot>
              <tr>
                <td colSpan={2} className={styles.totalLabel}>
                  TOTALS (page {page + 1})
                </td>
                <td />
                <td />
                <td className={styles.totalNum}>${totals.sub.toFixed(2)}</td>
                <td
                  className={`${styles.totalNum} ${
                    totals.pro < 0 ? styles.totalRd : ''
                  }`}
                >
                  {totals.pro < 0 ? fmtC(totals.pro) : '—'}
                </td>
                <td className={styles.totalNum}>
                  {totals.adjAmt !== 0 ? fmtC(totals.adjAmt) : '—'}
                </td>
                <td className={`${styles.totalNum} ${styles.totalOr}`}>
                  ${totals.sur.toFixed(2)}
                </td>
                <td className={`${styles.totalNum} ${styles.totalOr}`}>
                  ${totals.sale.toFixed(2)}
                </td>
                <td className={`${styles.totalNum} ${styles.totalRd}`}>
                  -{totals.fee.toFixed(2)}
                </td>
                <td className={styles.totalPayout}>${totals.pay.toFixed(2)}</td>
                {showKravein && (
                  <>
                    <td className={`${styles.kraveinCol} ${styles.totalNum}`}>
                      {totals.adj !== 0 ? fmtC(totals.adj) : '—'}
                    </td>
                    <td className={`${styles.kraveinCol} ${styles.totalNum}`}>
                      {totals.del !== 0 ? `$${totals.del.toFixed(2)}` : '—'}
                    </td>
                    <td className={`${styles.kraveinCol} ${styles.totalNum}`}>
                      ${totals.cc.toFixed(2)}
                    </td>
                    <td className={`${styles.kraveinCol} ${styles.totalNum}`}>
                      {fmtC(totals.kraveinPayout)}
                    </td>
                  </>
                )}
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={`button ${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
          >
            ← Prev
          </button>
          <span className={styles.pageInfo}>
            Page {page + 1} of {totalPages}
          </span>
          <button
            className={`button ${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
            disabled={page >= totalPages - 1}
            onClick={() => setPage(page + 1)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default OrdersTable;
